import { useEffect, useState } from 'react';
import Modal from 'ui3/Modal/Modal';
import Button from 'ui3/Button/Button';
import Text from 'ui3/Text/Text';
import {
  GetFacebookPages_getFacebookPages,
  GetFacebookPages_getFacebookPages_pages,
} from 'api/social-media-connections/types/GetFacebookPages';
import { getProperErrorMessage } from 'helpers/errors';
import {
  getSocialIntegrationErrorDialog,
  SocialIntegrationErrorDialogData,
} from 'helpers/social-integration-errors';
import { useCompleteFacebookAuth } from 'hooks/useCompleteFacebookAuth';
import { showToast } from 'components/common/Toast/Toast';
import styles from './SelectFacebookPageModal.module.scss';

type SelectFacebookPageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  pagesData?: GetFacebookPages_getFacebookPages;
  onConfirm?: (page: GetFacebookPages_getFacebookPages_pages) => void;
  onError?: (error: SocialIntegrationErrorDialogData) => void;
  loading?: boolean;
};

const SelectFacebookPageModal = ({
  isOpen,
  onClose,
  pagesData,
  onConfirm,
  onError,
  loading = false,
}: SelectFacebookPageModalProps) => {
  const [
    selectedPage,
    setSelectedPage,
  ] = useState<GetFacebookPages_getFacebookPages_pages | null>(null);

  const [
    completeFacebookAuth,
    { loading: isCompletingAuth },
  ] = useCompleteFacebookAuth();

  const handleConfirm = async () => {
    if (selectedPage && pagesData?.sessionId) {
      onConfirm?.(selectedPage);
      try {
        await completeFacebookAuth({
          variables: {
            input: {
              sessionId: pagesData.sessionId,
              pageId: selectedPage.id,
            },
          },
        });
        onClose();
        setSelectedPage(null);
      } catch (error) {
        console.error(error);

        const errorDialog = getSocialIntegrationErrorDialog(error);
        if (errorDialog && onError) {
          onClose();
          setSelectedPage(null);
          onError(errorDialog);
          return;
        }

        showToast({
          type: 'error',
          message:
            errorDialog?.message ||
            getProperErrorMessage(error, 'Something went wrong'),
        });
      }
    }
  };

  const handleClose = () => {
    if (isCompletingAuth) {
      return;
    }

    onClose();
    setSelectedPage(null);
  };

  useEffect(() => {
    if (!selectedPage) {
      setSelectedPage(pagesData?.pages?.[0] ?? null);
    }
  }, [pagesData?.pages, selectedPage]);

  const hasPages = pagesData?.pages && pagesData.pages.length > 0;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={hasPages ? 'Select Facebook Page' : 'No Pages Available'}
      subTitle={
        hasPages
          ? 'Choose which Facebook page you want to connect to your profile'
          : 'The selected pages require proper permissions'
      }
      size="small"
      closeOnOverlayClick={false}
      showCloseIcon={false}
    >
      <div className={styles.content}>
        {!pagesData?.pages || pagesData.pages.length === 0 ? (
          <>
            <div className={styles.emptyState}>
              <Text
                variant="body1Regular16"
                color="lights-low"
                textAlign="center"
              >
                The pages you selected don&apos;t have the proper roles or
                permissions to be connected. Please select other pages with
                admin access or ensure you have the required permissions, then
                try again.
              </Text>
            </div>
            <div className={styles.actions}>
              <Button
                variant="primary"
                onClick={handleClose}
                block
                disabled={loading}
              >
                Got it
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.pagesList}>
              {pagesData.pages.map((page) => (
                <label key={page.id} className={styles.pageItem}>
                  <input
                    type="radio"
                    name="facebookPage"
                    value={page.id}
                    checked={selectedPage?.id === page.id}
                    onChange={() => setSelectedPage(page)}
                    className={styles.radio}
                  />
                  <div className={styles.pageInfo}>
                    {page.picture && (
                      <img
                        src={page.picture}
                        alt={page.name}
                        className={styles.pageLogo}
                      />
                    )}
                    <div className={styles.pageDetails}>
                      <Text variant="subtitle2">{page.name}</Text>
                      {page.username && (
                        <Text variant="body2Regular" color="lights-low">
                          @{page.username}
                        </Text>
                      )}
                      {page.followersCount !== null && (
                        <Text variant="body2Regular" color="lights-low">
                          {page.followersCount.toLocaleString()} followers
                        </Text>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className={styles.actions}>
              <Button
                variant="primary"
                onClick={handleConfirm}
                block
                disabled={!selectedPage || isCompletingAuth}
                loading={loading || isCompletingAuth}
              >
                Connect Page
              </Button>
              <Button
                variant="secondary"
                onClick={handleClose}
                block
                disabled={loading || isCompletingAuth}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default SelectFacebookPageModal;
